import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { store } from "../store";
import { numberWithCommas } from "../CustomMath";
import MailService from "../mailService";
import { getFirebaseImageUrl } from "../firebase";
import "../assets/css/cart.css";
import { useTranslation } from "language-maps/use-translation";

const initialState = {
  id: 0,
  title: "",
  firstName: "",
  lastName: "",
  telephone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  distance: 0,
  totalQty: 0,
  totalWidth: 0,
  subTotal: 0,
  totalPleats: 0,
  transportationCost: 0,
  fixingCost: 0,
  totalFabricCost: 0,
  total: 0,
  products: [],
};

const Cart = () => {
  const [state, setState] = useState({ ...initialState });
  const [dialog, setDialog] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [transport, setTransport] = useState(false);
  const [distance, setDistance] = useState("");
  const { t } = useTranslation();

  const service = new MailService();

  useEffect(() => {
    const items = store.getState().items;

    const temp = { ...initialState };
    temp.products = items;

    calculateTotal(temp);
  }, []);

  function calculateFixingCost(e) {
    setFixing(e.value);

    const temp = state;
    temp.fixingCost = 0;

    if (e.value) {
      let blindQty = 0;
      let curtainQty = 0;
      (state.products || []).forEach((p) => {
        const qty = +p.item.quantity;
        if (p.item.type === "Blind") blindQty += qty;
        else curtainQty += qty;
      });
      const blindFixing = blindQty * 250;
      const curtainFixing = curtainQty > 0
        ? (curtainQty <= 2 ? 250 : 200) * curtainQty
        : 0;
      temp.fixingCost = blindFixing + curtainFixing;
    }

    setState((prevState) => ({ ...prevState, fixingCost: temp.fixingCost }));
    calculateTotal(temp);
  }

  function calculateTransportCost(e) {
    if (isNaN(e.target.value)) return;

    setDistance(e.target.value);

    const temp = state;
    temp.transportationCost = e.target.value * 175;

    setState((prevState) => ({
      ...prevState,
      transportationCost: temp.transportationCost,
    }));

    calculateTotal(temp);
  }

  function toggleDistance(e) {
    setTransport(e.value);
    setDistance("");
    const temp = { ...state, transportationCost: 0 };
    setState(temp);
    calculateTotal(temp);
  }

  const getLineTotalAfterItemDiscount = (item) => {
    const designDiscount = item.designDiscountAmount || 0;
    return +item.total - designDiscount;
  };

  const calculateTotal = (tempState) => {
    if (tempState.products) {
      tempState.totalFabricCost = 0;
      tempState.subTotal = 0;
      tempState.totalWidth = 0;
      tempState.totalQty = 0;
      tempState.totalPleats = 0;

      tempState.products.forEach(function (p) {
        tempState.totalFabricCost += p.item.fabricCost || 0;
        tempState.subTotal += getLineTotalAfterItemDiscount(p.item);

        if (p.item.isPole) {
          tempState.totalWidth += p.item.quantity * p.item.width + 12;
        }

        tempState.totalQty += +p.item.quantity;
        tempState.totalPleats += +(p.item.pleats || 0) * p.item.quantity;
        tempState.total = tempState.subTotal + tempState.fixingCost + tempState.transportationCost;
      });
    }

    setState(tempState);
  };

  const removeItem = (id) => {
    store.dispatch({
      type: "itemRemoved",
      payload: id,
    });

    const updatedProducts = state.products.filter((rem) => rem.id !== id);

    setState((prevState) => ({
      ...prevState,
      products: updatedProducts,
    }));

    const tempState = { ...state, products: updatedProducts };
    calculateTotal(tempState);
  };

  const clearCart = () => {
    store.dispatch({
      type: "itemsCleared",
      payload: [],
    });

    const tempState = { ...initialState };

    setState(tempState);
    calculateTotal(tempState);
  };

  const getTotalItemDiscount = () => {
    if (!state.products) return 0;
    return state.products.reduce(
      (sum, p) => sum + (p.item.designDiscountAmount || 0),
      0
    );
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    const temp = { ...state };
    temp[name] = value;
    if (name === "distance" && state.transportCostEnabled) {
      temp["transportationCost"] = value * 175;
    }
    setState(temp);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMail();
  };

  const sendMail = async () => {
    const payload = { ...state };

    const order = {
      customerDetails: {
        title: payload.title,
        firstName: payload.firstName,
        lastName: payload.lastName,
        addressLine1: payload.addressLine1,
        addressLine2: payload.addressLine2,
        email: payload.email,
        telephone: payload.telephone,
      },
      orderItems: [],
      orderDetails: {
        distance: distance || 0,
        fixingCost: payload.fixingCost,
        subTotal: payload.subTotal + getTotalItemDiscount(),
        total: payload.total,
        totalFabricCost: payload.totalFabricCost,
        totalPleats: payload.totalPleats,
        totalQty: payload.totalQty,
        totalWidth: inchesToFeet(payload.totalWidth),
        transportationCost: payload.transportationCost,
        discountName: "Item Discounts",
        discountAmount: getTotalItemDiscount(),
      },
    };

    payload.products.forEach((element) => {
      const lineTotal = getLineTotalAfterItemDiscount(element.item);
      const orderItem = {
        selectedDesign: element.item.selectedDesign,
        cost: element.item.cost,
        fabricCost: element.item.fabricCost || 0,
        fabricPrice: element.item.fabricPrice || 0,
        height: element.item.height,
        image: element.item.image,
        poleRequired: element.item.isPole,
        pleats: element.item.pleats || 0,
        price: element.item.price,
        quantity: element.item.quantity,
        total: lineTotal,
        type: element.item.type,
        width: element.item.width,
        discountAmount: element.item.designDiscountAmount || 0,
        discountName: element.item.discountName || "",
      };
      if (element.item.type === "Blind") {
        orderItem.wallMount = element.item.wallMount || "";
        orderItem.pullCordSide = element.item.pullCordSide || "";
        orderItem.frameColor = element.item.frameColor || "";
      }
      // Always include front/back pleats; they'll be 0 for non-double curtains
      orderItem.frontPleats = element.item.frontPleats || 0;
      orderItem.backPleats = element.item.backPleats || 0;
      order.orderItems.push(orderItem);
    });

    setDialog(true);

    const result = await service.postToEndPoint(order);
    if (result.id) clearCart();

    setDialog(false);
  };

  const inchesToFeet = (inches) => {
    return inches ? Math.ceil(inches / 12) : 0;
  };

  const renderListItem = (dt) => {
    const item = dt.item.selectedDesign;
    return (
      <div className="grid mb-2 lg:mb-4">
        <div
          className="col-12 lg:col-3"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            className="cart-item-image"
            src={getFirebaseImageUrl(item.Image)}
            alt="not found"
            loading="lazy"
          />
        </div>
        <div className="col-12 lg:col-9 flex-1 flex justify-content-between lg:ml-4">
          <div className="flex flex-column gap-2">
            <h4>{item.Name}</h4>
            <h5 className="item-specs">Item Code: {item.Code}</h5>
            <div>{renderItemSpecs(dt.item)}</div>
            <div className="item-specs">
              {t("Unit Price")}: LKR {numberWithCommas(dt.item.cost)}
            </div>
            <div className="item-specs">
              {t("Quantity")}: {dt.item.quantity}
            </div>
            {dt.item.designDiscountAmount > 0 && (
              <div className="item-specs" style={{ color: "#d32f2f" }}>
                {t("Item discount")}: - LKR {numberWithCommas(dt.item.designDiscountAmount)}
              </div>
            )}
            <div className="item-specs" style={{ fontWeight: 900 }}>
              {t("Total")}: LKR {numberWithCommas(getLineTotalAfterItemDiscount(dt.item))}
            </div>
            <Button
              style={{ height: "36px" }}
              icon="pi pi-trash"
              className="p-button-secondary mobile-specs-delete-btn"
              onClick={() => removeItem(dt.id)}
            />
          </div>
          <div>
            <Button
              icon="pi pi-trash"
              rounded
              className="p-button-secondary"
              onClick={() => removeItem(dt.id)}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderChip = (label, value) => (
    <div className="chip">
      <div>
        <span>{t(label)}</span>
      </div>
      <div
        className="p-mx-2"
        style={{ height: "1px", backgroundColor: "black" }}
      ></div>
      <div className="text-center">{value}</div>
    </div>
  );

  const renderItemSpecs = (item) => {
    // Interior Designs are fixed-size finished products — no measurements to show.
    if (item.type === "Interior Design") {
      return (
        <div className="flex lg:gap-4 gap-2">
          {renderChip("Type", t("Interior Design"))}
        </div>
      );
    }
    if (item.type === "Blind") {
      return (
        <div className="flex lg:gap-4 gap-2">
          {renderChip("Type", item.type)}
          {renderChip("Width", item.width)}
          {renderChip("Height", item.height)}
          {renderChip("Mount", t(item.wallMount || "Inside"))}
          {renderChip("Pull Cord", t(item.pullCordSide || "Right"))}
          {renderChip("Frame Color", t(item.frameColor || "A suitable one"))}
        </div>
      );
    }
    return (
      <div className="flex lg:gap-4 gap-2">
        {renderChip("Type", item.type)}
        {renderChip("Width", item.width)}
        {renderChip("Height", item.height)}
        {renderChip("Pleats", item.pleats)}
        {renderChip("Pole", item.isPole ? t("Yes") : t("No"))}
      </div>
    );
  };

  const renderAdditionalServices = () => {
    return (
      <div className="card flex flex-column mb-5">
        <div className="step">
          <label>{t("Step")}</label>
          <label>2/3</label>
        </div>
        <h3 className="text-center">{t("Additional Services")}</h3>
        <div className="flex flex-column gap-2 lg:mx-4 my-3">
          <hr />
          <div className="flex justify-content-between">
            <label>{t("Fixing Required")}</label>
            <InputSwitch
              checked={fixing}
              onChange={(e) => calculateFixingCost(e)}
            />
          </div>
          <hr />
          <div className="flex justify-content-between align-items-center">
            <label>{t("Transport Required")}</label>
            <div className="flex gap-2 align-items-center">
              {transport ? (
                <InputText
                  required
                  value={distance}
                  onChange={(e) => calculateTransportCost(e)}
                  placeholder={`${t("Distance")}: KM`}
                  className="p-inputtext-sm"
                  style={{ maxWidth: "120px" }}
                />
              ) : null}
              <InputSwitch
                checked={transport}
                onChange={(e) => toggleDistance(e)}
              />
            </div>
          </div>
          <hr />
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    return (
      <div className="card">
        <div className="step">
          <label>{t("Step")}</label>
          <label>3/3</label>
        </div>
        <h3 className="text-center">{t("Order Summary")}</h3>
        <div className="card summary-card lg:m-4">
          <div className="grid">
            <div className="col-12 lg:col-6 flex flex-column gap-3 lg:p-5 left-column">
              <div className="flex justify-content-between">
                <label>{t("Total Quantities")}</label>
                <label>{state.totalQty}</label>
              </div>
              <div className="flex justify-content-between">
                <label>{t("Pole Width")}</label>
                <label>{inchesToFeet(state.totalWidth)} ft</label>
              </div>
              <div className="flex justify-content-between">
                <label>{t("Total Pleats")}</label>
                <label>{state.totalPleats}</label>
              </div>
              <hr />
              <div className="flex justify-content-between">
                <label>{t("Subtotal")}</label>
                <label>LKR {numberWithCommas(state.subTotal + getTotalItemDiscount())}</label>
              </div>
              {fixing && (
                <div className="flex justify-content-between">
                  <label>{t("Fixing Cost")}</label>
                  <label>LKR {numberWithCommas(state.fixingCost)}</label>
                </div>
              )}
              {transport && (
                <div className="flex justify-content-between">
                  <label>{t("Transportation Cost")}</label>
                  <label>LKR {numberWithCommas(state.transportationCost)}</label>
                </div>
              )}
              {getTotalItemDiscount() > 0 && (
                <div className="flex justify-content-between" style={{ color: "#d32f2f" }}>
                  <label>{t("Item Discounts")}</label>
                  <label>- LKR {numberWithCommas(getTotalItemDiscount())}</label>
                </div>
              )}
              <hr />
              <div className="cart-total">
                <span>{t("Grand Total")}</span>
                <span>
                  LKR {numberWithCommas(state.total)}
                </span>
              </div>
            </div>
            <div className="col-12 lg:col-6 flex flex-column pt-4 lg:p-5">
              <strong>
                {t("Please fill your details to get your quotation.")}
              </strong>
              <strong>{t("You will receive the quote via an Email")}</strong>
              <form onSubmit={onSubmit} className="flex flex-column gap-3 mt-3">
                <div className="flex justify-content-between">
                  <InputText
                    name="title"
                    required
                    value={state.title}
                    onChange={onChange}
                    placeholder={t("Title")}
                    style={{ width: "60px" }}
                  />
                  <InputText
                    className="cart-names"
                    name="firstName"
                    required
                    value={state.firstName}
                    onChange={onChange}
                    placeholder={t("First Name")}
                  />
                  <InputText
                    className="cart-names"
                    name="lastName"
                    value={state.lastName}
                    onChange={onChange}
                    placeholder={t("Last Name")}
                  />
                </div>
                <InputText
                  name="telephone"
                  type="tel"
                  required
                  value={state.telephone}
                  onChange={onChange}
                  placeholder={t("Telephone / Whatsapp Number")}
                />
                <InputText
                  name="email"
                  type="email"
                  required
                  value={state.email}
                  onChange={onChange}
                  placeholder={t("Email")}
                />
                <InputText
                  name="addressLine1"
                  value={state.addressLine1}
                  required
                  onChange={onChange}
                  placeholder={t("Address Line 1")}
                />
                <InputText
                  name="addressLine2"
                  value={state.addressLine2}
                  onChange={onChange}
                  placeholder={t("Address Line 2")}
                />
                <Button
                  className="p-button-secondary"
                  label={t("Get a Quotation")}
                  type="submit"
                ></Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMyCart = () => {
    return (
      <div className="card flex flex-column mb-5">
        <div className="step">
          <label>{t("Step")}</label>
          <label>1/3</label>
        </div>
        <h3 className="text-center">My Cart</h3>
        <h5 className="mb-7 text-center">
          {t("Get a quotation for the items in the cart")}
        </h5>
        <div className="my-3 lg:mx-4">
          {state.products.map((item, i) => (
            <React.Fragment key={i}>{renderListItem(item)}</React.Fragment>
          ))}
          <Button
            label={t("Clear Cart")}
            className="p-button-secondary"
            type="button"
            onClick={clearCart}
            style={{ float: "right" }}
          ></Button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ marginBottom: "6rem" }}>
      <div className="content-wrapper">
        <h1 className="mt-4 text-center">{t("Cart")}</h1>
        {renderMyCart()}
        {renderAdditionalServices()}
        {renderSummary()}
      </div>
      <Dialog
        visible={dialog}
        style={{ width: "90vw", maxWidth: "300px", textAlign: "center" }}
        closable={false}
      >
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
        <h6>{t("Please wait ...")}</h6>
      </Dialog>
    </div>
  );
};

export default Cart;
